import { Box, display } from '@mui/system'
import React, { useState } from 'react'
import { PropTypes } from 'prop-types'
import { Button, Divider, Stack, SvgIcon, Typography } from '@mui/material'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { currencyFormat } from 'src/utils';
import { readableInk } from 'src/utils/colour';
import { ReactComponent as flagIcon } from '../../assets/images/flag.svg'

const CustomCareerBar = ({ isWidget,onWidgetCareerClick, career, barColor, barWidth, addToCompare, compareCareersState, showCareerDetailsPopup }) => {
    const [styleData, setStyleData] = useState({})
    // the line before the arrow: the sector colour in the widget, white while the row is filled
    const ink = readableInk(barColor)
    const many = career?.coursesAvailable > 1
    let coursesWord = many ? 'Courses Available' : 'Course Available'
    if (isWidget) coursesWord = many ? 'courses' : 'course'
    const dividerSx = isWidget ? { bgcolor: 'transparent', borderColor: `${barColor} !important` } : { bgcolor: 'white !important', borderColor: 'unset' }
    
    return (
        <Box
            className="sub-bar"
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={isWidget ? {
                // widget: an outlined row inside its sector's group; it fills with the sector colour when pointed at
                boxSizing: 'border-box',
                width: '100% !important',
                margin: '0 !important',
                backgroundColor: 'transparent',
                border: 0,
                borderRadius: '10px',
                color: '#1b2230 !important',
                transition: 'background-color .15s, color .15s',
                '&:hover, &:focus-within': { backgroundColor: barColor, color: `${ink} !important` },
                '&:hover hr, &:focus-within hr': { borderColor: `${ink} !important`, opacity: 0.45 },
                '& a:focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px', borderRadius: '2px' },
                '& .tsw-row-arrow': { transition: 'transform .15s' },
                '&:hover .tsw-row-arrow, &:focus-within .tsw-row-arrow': { transform: 'translateX(3px)' },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '& .tsw-row-arrow': { transition: 'none' } },
            } : { backgroundColor: barColor, width:{xs: '100%', sm: barWidth} }}
        >
            <Box sx={isWidget ? { flex: 1, minWidth: 0, maxWidth: 'none !important' } : undefined}>
                {/* in the widget everything is on one line (name, pay, time, courses) and wraps only when the row is too narrow */}
                <Stack
                    sx={{ ml: 2, mr: isWidget ? 1.5 : 0, py: isWidget ? 0.5 : 2 }}
                    direction={isWidget ? 'row' : 'column'}
                    flexWrap={isWidget ? 'wrap' : 'nowrap'}
                    alignItems={isWidget ? 'center' : undefined}
                    columnGap={isWidget ? 2 : 0}
                    rowGap={isWidget ? 0.25 : 0}
                >
                    <Typography sx={{ alignSelf: isWidget ? 'center' : 'start', ...(isWidget ? { flex: '1 1 150px', minWidth: 0 } : {}), fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, textAlign: 'start', fontWeight: 600 }}>
                        {isWidget ? (
                            // In the widget the title is a link that opens the same career details as the arrow on the right
                            <a
                                href={`#career-${career?.id}`}
                                onClick={(event) => { event.preventDefault(); showCareerDetailsPopup(career.id); }}
                                style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                            >
                                {career?.name}
                            </a>
                        ) : career?.name}
                    </Typography>
                    <Stack
                        direction={isWidget ? 'row' : { md: 'column', lg: 'row' }}
                        flexWrap={isWidget ? 'wrap' : 'nowrap'}
                        columnGap={isWidget ? 1 : { md: 0, lg: 2 }}
                        rowGap={isWidget ? 0.25 : 0}
                        // widget: the three details sit in fixed columns, so they line up from one row to the next
                        sx={isWidget ? { flex: '0 0 auto', '& > .MuiStack-root': { opacity: 0.88 }, '& > .MuiStack-root:nth-of-type(1)': { minWidth: 80 }, '& > .MuiStack-root:nth-of-type(2)': { minWidth: 116 }, '& > .MuiStack-root:nth-of-type(3)': { minWidth: 82 } } : undefined}
                    >
                        <Stack direction='row' alignItems="center" gap={0.5}>
                            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: '18px' }} />
                            <Typography sx={{ fontSize: '0.9em !important', fontWeight: '300' }} component='small'>
                                {currencyFormat(career?.salary)}
                            </Typography>
                        </Stack>

                        <Stack direction='row' alignItems="center" gap={0.5}>
                            <UpdateOutlinedIcon sx={{ fontSize: '18px' }} />
                            <Typography sx={{ fontSize: '0.9em !important', fontWeight: '300' }}>
                                {career?.transferWindow}
                            </Typography>
                        </Stack>
                        <Stack direction='row' alignItems="center" gap={0.5}>
                            <SchoolOutlinedIcon sx={{ fontSize: '18px' }} />
                            <Typography sx={{ fontSize: '0.9em !important', fontWeight: '300' }}>
                                {career?.coursesAvailable} {coursesWord}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
            <Box
                flex={isWidget ? 0 : 1}
            />
            {(career?.careerType !== 'CAREER_GOAL' && career?.careerType !== 'CURRENT_ROLE') ? <>
                <Divider sx={dividerSx} orientation="vertical" flexItem />
               {!isWidget && <Box display='flex'
                    sx={{
                        paddingLeft:'12px',
                        paddingRight:'12px',
                        fontWeight: 200,
                        cursor: 'pointer',
                        height: '100%'
                    }}
                    onClick={() => addToCompare(career?.id)}
                    alignItems='center'>

                    {compareCareersState && compareCareersState.find(currentCareer => currentCareer === career.id) ?
                        // (<RemoveCircleOutlineOutlinedIcon sx={{ fontSize: '18px', mx:1 }} /> ):
                        <Stack direction='row' alignItems='center'>
                            <RemoveCircleOutlineOutlinedIcon sx={{ mx: { sx: 0, sm: 0, md: 0.5 } }} />
                            <Typography sx={{ display: { sm: 'none', xs: 'none', md: 'block' }, width: '7ch' }}>Remove</Typography>
                        </Stack> :
                        <Stack direction='row' alignItems='center'>
                            <AddCircleOutlineOutlinedIcon sx={{ mx: { sx: 0, sm: 0, md: 0.5 } }} />
                            <Typography sx={{ display: { sm: 'none', xs: 'none', md: 'block' }, width: '7ch' }}>Compare</Typography>
                        </Stack>
                    }
                </Box>}
                <Divider sx={dividerSx} orientation="vertical" flexItem />
            </> :
                <>
                    <Divider sx={dividerSx} orientation="vertical" flexItem />
                    <Box display='flex'
                        sx={{
                            // px: 2,
                            fontWeight: 200,
                            cursor: 'pointer',
                            height: '100%',
                        }}>
                        {career?.careerType === 'CURRENT_ROLE' ?
                            // <Typography fontStyle='italic'>
                            //     Current Role
                            // </Typography> 
                            <>
                                {/* <Box mx={1.5} sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
                                    <SvgIcon
                                        component={flagIcon}
                                        inheritViewBox
                                    />
                                </Box> */}
                                {/* <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' }, px: 2 }}> */}
                                <Box sx={{ width: {md:'11.3ch', lg:'10.7ch' }, textAlign:'center'}} mx={1.5}>
                                    {/* <Typography sx={{ width: '10ch' }} fontStyle='italic'>
                                        Current Role
                                    </Typography> */}
                                    <BadgeIcon />
                                </Box>
                            </>
                            :
                            <>
                                <Box sx={{ width: {md:'11.3ch', lg:'10.7ch' }, textAlign:'center'}} mx={1.5}
                                //  sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}
                                 >
                                    <SvgIcon
                                        component={flagIcon}
                                        inheritViewBox
                                    />
                                </Box>
                                <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none' }, px: 2 }}>
                                    <Typography fontStyle='italic'>
                                        Career Goal
                                    </Typography>
                                </Box>
                            </>

                        }
                    </Box>
                    <Divider sx={dividerSx} orientation="vertical" flexItem />
                </>
            }
            <Box sx={{ cursor: 'pointer', height: isWidget ? '38px' : '80px', display: 'flex', alignItems: 'center' }} onClick={() => !isWidget ? showCareerDetailsPopup(career.id) : showCareerDetailsPopup(career.id)}>
                <ChevronRightIcon className='tsw-row-arrow' sx={{ mx: 1 }} fontSize='medium' />
            </Box>
        </Box>
    )
}

export default CustomCareerBar

CustomCareerBar.propTypes = {
    career: PropTypes.object,
    barColor: PropTypes.string,
    barWidth: PropTypes.number,
    addToCompare: PropTypes.func,
    compareCareersState: PropTypes.array,
    showCareerDetailsPopup: PropTypes.func,
    onWidgetCareerClick: PropTypes.func,
    isWidget: PropTypes.bool,
}